/**
 * Shared button variant styles for Button and LinkButton components.
 *
 * These compound variants are identical between Button and LinkButton,
 * except for disabled state handling:
 * - Button uses `disabled:` (for <button> elements)
 * - LinkButton uses `data-[disabled]:` (for <a> elements via react-aria)
 */

export const buttonVariants = {
  variant: {
    primary: "",
    secondary: "",
    tertiary: "",
    glass: "",
    negative: "",
    "app-selector": "",
    dropdown: "",
    plain: "",
    link: "",
  },
  uiVersion: {
    v1: "",
    v2: "",
  },
} as const;

export const buttonCompoundVariants = [
  // V2 styling
  {
    uiVersion: "v2" as const,
    variant: "primary" as const,
    class: `
      bg-interactive-primary-button-enabled-background
      hover:bg-interactive-primary-button-hover-background
      focus:bg-interactive-primary-button-focus-background
      disabled:bg-interactive-disabled-background disabled:text-interactive-disabled-foreground`,
  },
  {
    uiVersion: "v2" as const,
    variant: "secondary" as const,
    class: `
      border-interactive-secondary-button-enabled-outline
      text-interactive-secondary-button-enabled-foreground
      bg-interactive-secondary-button-enabled-background
      hover:bg-interactive-secondary-button-hover-background
      focus:bg-interactive-secondary-button-focus-background
      disabled:border-interactive-disabled-outline disabled:bg-transparent disabled:text-interactive-disabled-foreground`,
  },
  {
    uiVersion: "v2" as const,
    variant: "tertiary" as const,
    class: `
      text-interactive-tertiary-button-enabled-foreground
      bg-transparent
      hover:bg-interactive-tertiary-button-hover-background
      hover:text-interactive-tertiary-button-hover-foreground
      focus:text-interactive-tertiary-button-focus-foreground
      focus:bg-interactive-tertiary-button-focus-background
      disabled:text-interactive-disabled-foreground disabled:hover:bg-transparent`,
  },
  {
    uiVersion: "v2" as const,
    variant: "glass" as const,
    class: `
      bg-interactive-glass-button-enabled-background
      border-interactive-glass-button-enabled-outline
      text-interactive-glass-button-enabled-foreground
      hover:text-interactive-glass-button-hover-foreground
      hover:bg-interactive-glass-button-hover-background
      hover:border-interactive-glass-button-hover-outline
      focus:bg-interactive-glass-button-focus-background focus:text-interactive-glass-button-focus-foreground focus:border-interactive-glass-button-focus-outline backdrop-blur hover:backdrop-blur-xl  focus:backdrop-blur-2xl  disabled:bg-interactive-disabled-background disabled:text-interactive-disabled-foreground`,
  },
  {
    uiVersion: "v2" as const,
    variant: "negative" as const,
    class: `
      text-interactive-negative-button-enabled-foreground
      bg-interactive-negative-button-enabled-background
      hover:bg-interactive-negative-button-hover-background
      hover:text-interactive-negative-button-hover-foreground
      focus:text-interactive-negative-button-focus-foreground
      focus:bg-interactive-negative-button-focus-background
      disabled:text-interactive-disabled-foreground disabled:hover:bg-interactive-negative-button-enabled-background`,
  },
  {
    uiVersion: "v2" as const,
    variant: "app-selector" as const,
    class: `
      flex items-center text-label-medium
      hover:bg-interactive-primary-button-enabled-background
      hover:text-interactive-primary-button-enabled-foreground
      outline-none justify-center py-1 px-3 gap-1 rounded-10 hover:border-interactive-primary-button-enabled-background bg-neutral-white transition text-interactive-secondary-button-enabled-foreground`,
  },
  {
    uiVersion: "v2" as const,
    variant: "dropdown" as const,
    class: `
    py-3xs px-3xs rounded-sm
    text-label-medium text-text-primary font-label-medium font-normal
    hover:bg-interactive-secondary-button-focus-background
    focus:bg-interactive-secondary-button-focus-background`,
  },
  {
    uiVersion: "v2" as const,
    variant: "plain" as const,
    class: `
      text-text-primary bg-transparent
      disabled:text-neutral-200`,
  },
  {
    uiVersion: "v2" as const,
    variant: "link" as const,
    class: `
      p-0 border-0 rounded-none font-normal bg-transparent
      text-interactive-link-enabled underline
      hover:text-interactive-link-hover
      focus:text-interactive-link-focus
      disabled:text-interactive-link-disabled disabled:cursor-not-allowed`,
  },

  // V1 styling
  {
    uiVersion: "v1" as const,
    variant: "primary" as const,
    class: `
    rounded-xs text-text-primary-inverse
    bg-legacy-orange-D10 border-legacy-orange-D10
    hover:bg-legacy-orange-D30 hover:border-legacy-orange-D30
    focus:bg-legacy-orange-D20 focus:border-legacy-orange-D20
    active:bg-legacy-dark-orange-D30 active:border-legacy-dark-orange-D30
    disabled:bg-legacy-orange-L70 disabled:border-legacy-orange-L70
    `,
  },
  {
    uiVersion: "v1" as const,
    variant: "secondary" as const,
    class: `
    rounded-xs text-center justify-center
    border-legacy-orange-D10 text-legacy-orange-D10 bg-transparent
    hover:border-legacy-orange-D30 hover:text-legacy-orange-D30
    focus:border-legacy-orange-D20 focus:text-legacy-orange-D20
    active:border-legacy-dark-orange-D30 active:text-legacy-dark-orange-D30
    disabled:border-legacy-orange-L70 disabled:text-legacy-orange-L70
    `,
  },
  {
    uiVersion: "v1" as const,
    variant: "tertiary" as const,
    class: `
    rounded-xs border border-legacy-navy-L50
    bg-transparent text-legacy-navy-L10
    hover:border-legacy-navy-L30
    focus:border-legacy-navy-L30
    active:border-legacy-navy-L20
    disabled:border-legacy-neutrals-denseFog disabled:text-legacy-neutrals-denseFog
    `,
  },
  {
    uiVersion: "v1" as const,
    variant: "negative" as const,
    class: `
    rounded-xs
    text-legacy-red-D20
    hover:text-legacy-red-D30 hover:border-legacy-red-D30
    focus:text-legacy-orange-D30 focus:border-legacy-orange-D30
    active:text-legacy-red-D40 active:border-legacy-red-D40
    disabled:text-legacy-red-L50 disabled:border-legacy-red-L50
    `,
  },
  {
    uiVersion: "v1" as const,
    variant: "plain" as const,
    class: `
    rounded-xs bg-transparent
    text-legacy-navy-L10
    `,
  },
];

export const buttonDefaultVariants = {
  variant: "primary" as const,
  uiVersion: "v2" as const,
};

/**
 * Compound variants for LinkButton with data-[disabled]: instead of disabled:.
 * Since LinkButton uses <a> elements which don't support :disabled pseudo-class,
 * react-aria uses data-disabled attribute instead.
 *
 * These are written explicitly (not transformed at runtime) so that
 * Tailwind's JIT compiler can detect the class names during source scanning.
 */
export const linkButtonCompoundVariants = [
  // V2 styling
  {
    uiVersion: "v2" as const,
    variant: "primary" as const,
    class: `
      bg-interactive-primary-button-enabled-background
      hover:bg-interactive-primary-button-hover-background
      focus:bg-interactive-primary-button-focus-background
      data-[disabled]:bg-interactive-disabled-background data-[disabled]:text-interactive-disabled-foreground`,
  },
  {
    uiVersion: "v2" as const,
    variant: "secondary" as const,
    class: `
      border-interactive-secondary-button-enabled-outline
      text-interactive-secondary-button-enabled-foreground
      bg-interactive-secondary-button-enabled-background
      hover:bg-interactive-secondary-button-hover-background
      focus:bg-interactive-secondary-button-focus-background
      data-[disabled]:border-interactive-disabled-outline data-[disabled]:bg-transparent data-[disabled]:text-interactive-disabled-foreground`,
  },
  {
    uiVersion: "v2" as const,
    variant: "tertiary" as const,
    class: `
      text-interactive-tertiary-button-enabled-foreground
      bg-transparent
      hover:bg-interactive-tertiary-button-hover-background
      hover:text-interactive-tertiary-button-hover-foreground
      focus:text-interactive-tertiary-button-focus-foreground
      focus:bg-interactive-tertiary-button-focus-background
      data-[disabled]:text-interactive-disabled-foreground  data-[disabled]:hover:bg-transparent`,
  },
  {
    uiVersion: "v2" as const,
    variant: "glass" as const,
    class: `
      bg-interactive-glass-button-enabled-background
      border-interactive-glass-button-enabled-outline
      text-interactive-glass-button-enabled-foreground
      hover:text-interactive-glass-button-hover-foreground
      hover:bg-interactive-glass-button-hover-background
      hover:border-interactive-glass-button-hover-outline
      focus:bg-interactive-glass-button-focus-background focus:text-interactive-glass-button-focus-foreground focus:border-interactive-glass-button-focus-outline backdrop-blur hover:backdrop-blur-xl  focus:backdrop-blur-2xl  data-[disabled]:bg-interactive-disabled-background data-[disabled]:text-interactive-disabled-foreground`,
  },
  {
    uiVersion: "v2" as const,
    variant: "negative" as const,
    class: `
      text-interactive-negative-button-enabled-foreground
      bg-interactive-negative-button-enabled-background
      hover:bg-interactive-negative-button-hover-background
      hover:text-interactive-negative-button-hover-foreground
      focus:text-interactive-negative-button-focus-foreground
      focus:bg-interactive-negative-button-focus-background
      data-[disabled]:text-interactive-disabled-foreground data-[disabled]:hover:bg-interactive-negative-button-enabled-background`,
  },
  {
    uiVersion: "v2" as const,
    variant: "app-selector" as const,
    class: `
      flex items-center text-label-medium
      hover:bg-interactive-primary-button-enabled-background
      hover:text-interactive-primary-button-enabled-foreground
      outline-none justify-center py-1 px-3 gap-1 rounded-10 hover:border-interactive-primary-button-enabled-background bg-neutral-white transition text-interactive-secondary-button-enabled-foreground`,
  },
  {
    uiVersion: "v2" as const,
    variant: "dropdown" as const,
    class: `
    py-3xs px-3xs rounded-sm
    text-label-medium text-text-primary font-label-medium font-normal
    hover:bg-interactive-secondary-button-focus-background
    focus:bg-interactive-secondary-button-focus-background`,
  },
  {
    uiVersion: "v2" as const,
    variant: "plain" as const,
    class: `
      text-text-primary bg-transparent
      data-[disabled]:text-neutral-200`,
  },
  {
    uiVersion: "v2" as const,
    variant: "link" as const,
    class: `
      p-0 border-0 rounded-none font-normal bg-transparent
      text-interactive-link-enabled underline
      hover:text-interactive-link-hover
      focus:text-interactive-link-focus
      data-[disabled]:text-interactive-link-disabled data-[disabled]:cursor-not-allowed`,
  },

  // V1 styling
  {
    uiVersion: "v1" as const,
    variant: "primary" as const,
    class: `
    rounded-xs text-text-primary-inverse
    bg-legacy-orange-D10 border-legacy-orange-D10
    hover:bg-legacy-orange-D30 hover:border-legacy-orange-D30
    focus:bg-legacy-orange-D20 focus:border-legacy-orange-D20
    active:bg-legacy-dark-orange-D30 active:border-legacy-dark-orange-D30
    data-[disabled]:bg-legacy-orange-L70 data-[disabled]:border-legacy-orange-L70
    `,
  },
  {
    uiVersion: "v1" as const,
    variant: "secondary" as const,
    class: `
    rounded-xs text-center justify-center
    border-legacy-orange-D10 text-legacy-orange-D10 bg-transparent
    hover:border-legacy-orange-D30 hover:text-legacy-orange-D30
    focus:border-legacy-orange-D20 focus:text-legacy-orange-D20
    active:border-legacy-dark-orange-D30 active:text-legacy-dark-orange-D30
    data-[disabled]:border-legacy-orange-L70 data-[disabled]:text-legacy-orange-L70
    `,
  },
  {
    uiVersion: "v1" as const,
    variant: "tertiary" as const,
    class: `
    rounded-xs border border-legacy-navy-L50
    bg-transparent text-legacy-navy-L10
    hover:border-legacy-navy-L30
    focus:border-legacy-navy-L30
    active:border-legacy-navy-L20
    data-[disabled]:border-legacy-neutrals-denseFog data-[disabled]:text-legacy-neutrals-denseFog
    `,
  },
  {
    uiVersion: "v1" as const,
    variant: "negative" as const,
    class: `
    rounded-xs
    text-legacy-red-D20
    hover:text-legacy-red-D30 hover:border-legacy-red-D30
    focus:text-legacy-orange-D30 focus:border-legacy-orange-D30
    active:text-legacy-red-D40 active:border-legacy-red-D40
    data-[disabled]:text-legacy-red-L50 data-[disabled]:border-legacy-red-L50
    `,
  },
  {
    uiVersion: "v1" as const,
    variant: "plain" as const,
    class: `
    rounded-xs bg-transparent
    text-legacy-navy-L10
    `,
  },
];
